/**
 * MarketingCampagnesPage — campagne-lijst + nieuwe campagne aanmaken.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMarketingStore, marketingFetch, type MarketingCampaign } from '../../stores/marketing-store';
import { MarketingNav, MarketingPageHeader, StatusBadge, formatDateTime } from './shared';

function NieuweCampagneModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { segments, templates, fetchSegments, fetchTemplates } = useMarketingStore();
  const [name, setName] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [subjectOverride, setSubjectOverride] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSegments();
    fetchTemplates();
  }, [fetchSegments, fetchTemplates]);

  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await marketingFetch<{ campaign: MarketingCampaign }>('/api/marketing/campaigns', {
        method: 'POST',
        body: {
          name,
          segment_id: segmentId,
          template_id: templateId,
          subject_override: subjectOverride || null,
        },
      });
      onCreated(data.campaign.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aanmaken mislukt');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Nieuwe campagne</h2>
        {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 mb-3">{error}</div>}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Naam</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="bv. Route-vul Zuid-Holland week 25"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Segment</label>
            <select
              value={segmentId}
              onChange={(e) => setSegmentId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">— kies segment —</option>
              {segments.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.member_count ?? '?'})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Template</label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">— kies template —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} (v{t.version})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Onderwerp (optioneel — anders template-onderwerp)</label>
            <input
              type="text"
              value={subjectOverride}
              onChange={(e) => setSubjectOverride(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
            Annuleren
          </button>
          <button
            onClick={create}
            disabled={saving || !name || !segmentId || !templateId}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Aanmaken…' : 'Aanmaken'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MarketingCampagnesPage() {
  const navigate = useNavigate();
  const { campaigns, isLoading, error, fetchCampaigns } = useMarketingStore();
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <MarketingPageHeader
          title="Campagnes"
          subtitle="Broadcast-campagnes met audience-preview en goedkeuring"
          actions={
            <button
              onClick={() => setShowNew(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
            >
              + Nieuwe campagne
            </button>
          }
        />
        <MarketingNav />

        <div className="p-6">
          {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}
          {isLoading && campaigns.length === 0 ? (
            <p className="text-sm text-gray-400">Laden…</p>
          ) : campaigns.length === 0 ? (
            <p className="text-sm text-gray-400">Nog geen campagnes — maak de eerste aan.</p>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
              {campaigns.map((c) => (
                <Link
                  key={c.id}
                  to={`/marketing/campagnes/${c.id}`}
                  className="flex items-center justify-between px-5 py-4 hover:bg-gray-50"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {c.audience_count != null ? `${c.audience_count} ontvangers · ` : ''}
                      aangemaakt {formatDateTime(c.created_at)}
                      {c.sent_at ? ` · verstuurd ${formatDateTime(c.sent_at)}` : ''}
                    </p>
                  </div>
                  <StatusBadge status={c.status} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {showNew && (
        <NieuweCampagneModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            navigate(`/marketing/campagnes/${id}`);
          }}
        />
      )}
    </>
  );
}
